// @/utils/learning/backgroundFlow.js
// 模块级单例：生命周期 = 页面生命周期，与弹窗组件无关。
// 组件销毁不影响流；组件重开时通过 getFlow 恢复现场。

import md from '@/utils/markdown/md';
import { handleMathData as rawHandleMathData } from '@/api/fetch/mathService';

// 后端忙碌/排队占位文案（从 AdvancedLearning.vue 迁入）
export const BUSY_TEXT_RE = /正在全力运转|很快就会?完成/;

// ---------- 状态容器 ----------
// key = `${kgid}|${level}`，同一时间一个图谱只保留一个活跃流
// value = {
//   type: 'node' | 'diag',
//   aiContent, controller,     // 流载体，后台继续跑
//   lastHtml,                  // 最近一次渲染结果（重开时先显示）
//   done, failed, errText,     // 终态
//   busy, emptyResult,         // 节点流专属终态分支
//   finalMsg,                  // 诊断流正常完成的最终文本
//   payload,                   // 诊断流提交的 payload（重开后可重建诊断链路）
//   pendingDiagDone,           // 诊断完成但收尾未做（等用户回来）
//   onDone,                    // 组件级收尾钩子（在场时才调）
// }
const flow = { tasks: {} };

// ---------- 订阅机制 ----------
const listeners = new Set();
export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function notify() {
  listeners.forEach(fn => { try { fn(); } catch (e) { console.warn(e); } });
}

// ---------- 基础 API ----------
export function getFlowKey(kgid, level) { return `${kgid}|${level}`; }
export function getFlow(key) { return flow.tasks[key] || null; }

export function updateFlow(key, patch) {
    const t = flow.tasks[key];
    if (t) { Object.assign(t, patch); notify(); }
}

// 终止并清除任务（abort 后下一次轮询发现 task 没了会自动停）
export function stopFlow(key, reason) {
  const t = flow.tasks[key];
  if (t) {
    if (t.lastHtml) snapshotFlow(key); // ★ 新增：中止/覆盖前保留现场
    if (t.controller) {
      try { t.controller.abort(); } catch (e) {}
      t.controller = null;
    }
    if (t._timer) clearTimeout(t._timer);
    delete flow.tasks[key];
  }
}

// 清理组件级钩子（组件销毁前调用，防止闭包持有已销毁组件）
export function detachFlowHooks(key) {
  const t = flow.tasks[key];
  if (t) t.onDone = null;
}

// ---------- 节点内容流 ----------
// config = { aiContent, controller, onDone(finalMsg) }
export function startNodeFlow(flowKey, config) {
    stopFlow(flowKey, '发起新一轮生成');
  
    const { aiContent, controller, onDone } = config;
    const task = {
      type: 'node',
      aiContent, controller, onDone,
      lastHtml: '', done: false, failed: false,
      errText: '', busy: false, emptyResult: false,
      _lastLen: -1,
    };
    flow.tasks[flowKey] = task;
  
    // ★ 原组件 onNodeClick 里的 waitDone 搬到这里，msg 读取改为全 part 拼接
    const waitDone = () => {
      const t = flow.tasks[flowKey];
      if (!t) return;                                   // 被 stopFlow 清除 → 停止轮询
  
      // ── 增量渲染：msg 变化即更新 lastHtml（等价原版 renderHandler 的流式 setContent）──
      const msg = getFlowMsg(t.aiContent);
      if (typeof msg === 'string' && msg && msg !== 'loading' && msg.length !== t._lastLen) {
        t._lastLen = msg.length;
        t.lastHtml = md.render(msg);
        scheduleSnapshot(flowKey); 
        notify();                                       // 在场组件立刻看到
      }
  
      // ── 终态判定（与原版 waitDone 条件一致）──
      if (t.aiContent.isDone === 1) {
        const finalMsg = getFlowMsg(t.aiContent);
        t.done = true;
  
        if (!finalMsg.trim()) {
          t.emptyResult = true;                         // "未获取到内容"
        } else if (BUSY_TEXT_RE.test(finalMsg)) {
          t.busy = true;                                // "请求排队中"
          t.lastHtml = '';                              // 忙碌占位不当作内容
        } else {
          t.lastHtml = md.render(finalMsg);             // 正常完成全文
        }
        markFlowUnseen(flowKey);                        // ★ 新增：完成 → 主窗口红点
        notify();
        if (t.onDone) t.onDone(finalMsg);               // 仅组件在场时有值
        return;
      }
  
      // ── 异常终态：isDone === 2/3（原版缺此分支会卡死，service 版补上）──
      if (t.aiContent.isDone && t.aiContent.isDone !== 0) {
        t.failed = true;
        t.errText = t.aiContent.isDone === 2 ? '回答已被终止'
                  : t.aiContent.isDone === 3 ? '网络异常' : '服务器异常';
        markFlowUnseen(flowKey);                        // ★ 新增：失败也提示用户回来看
        notify();
        if (t.onDone) t.onDone(getFlowMsg(t.aiContent));
        return;
      }
  
      // 定时器在 service，组件销毁不影响
      t._timer = setTimeout(waitDone, 300);
    };
    waitDone();
  
    return task;
  }

// ---------- 诊断流 ----------
// config = { aiContent, controller, payload, onDone(finalMsg|null, aiContent, errText|null) }
export function startDiagFlow(flowKey, config) {
    stopFlow(flowKey, '发起新一轮诊断');
  
    const { aiContent, controller, payload, onDone } = config;
    const task = {
      type: 'diag',
      aiContent, controller, payload, onDone,
      lastHtml: '', done: false, failed: false,
      errText: '', finalMsg: '',
      _lastLen: -1,
    };
    flow.tasks[flowKey] = task;
  
    // ★ 原组件 handleDiagTextClick 里的 renderLoop 搬到这里，msg 读取改为全 part 拼接
    const renderLoop = () => {
      const t = flow.tasks[flowKey];
      if (!t) return;
  
      // ── 增量渲染（等价原版 renderLoop 的流式 setContent）──
      const msg = getFlowMsg(t.aiContent);
      if (typeof msg === 'string' && msg !== 'loading' && msg.length !== t._lastLen) {
        t._lastLen = msg.length;
        t.lastHtml = md.render(msg);
        scheduleSnapshot(flowKey);
        notify();                                       // 在场组件立刻看到
      }
  
      // ── 终态判定（与原版 renderLoop 条件一致）──
      if (t.aiContent.isDone && t.aiContent.isDone !== 0) {
        const finalMsg = getFlowMsg(t.aiContent);
  
        if (t.aiContent.isDone === 1 && !BUSY_TEXT_RE.test(finalMsg) && finalMsg.trim()) {
          // 正常完成
          t.done = true;
          t.finalMsg = finalMsg;
          t.lastHtml = md.render(finalMsg);
          markFlowUnseen(flowKey);                      // ★ 新增：诊断轮完成 → 红点
          notify();
          if (t.onDone) t.onDone(finalMsg, t.aiContent, null);
        } else if (t.aiContent.isDone === 1 && BUSY_TEXT_RE.test(finalMsg)) {
          // 忙碌占位
          t.failed = true;
          t.errText = 'BUSY';
          t.lastHtml = '';
          notify();
        } else if (t.aiContent.isDone === 1 && !finalMsg.trim()) {
          // 完成但无内容
          t.failed = true;
          t.errText = 'EMPTY';
          notify();
        } else {
          // 异常/终止：isDone === 2 | 3 | 其它
          t.failed = true;
          t.errText = t.aiContent.isDone === 2 ? '回答已被终止'
                    : t.aiContent.isDone === 3 ? '网络异常' : '服务器异常';
          notify();
          if (t.onDone) t.onDone(null, t.aiContent, t.errText);
        }
        return;
      }
  
      t._timer = setTimeout(renderLoop, 300);
    };
    renderLoop();
  
    return task;
  }

// ★ 正文 = 所有 part 的 msg 拼接
// handleMathData 会在试题/plotly 处切分新 part，文本可能分布在多个 part 里，
// 只读 parts[0] 会漏掉后续内容（卡「加载中」的根因）
function getFlowMsg(aiContent) {
    if (!aiContent) return '';
    if (Array.isArray(aiContent.parts) && aiContent.parts.length) {
      const joined = aiContent.parts
        .map(p => (p && typeof p.msg === 'string') ? p.msg : '')
        .join('');
      if (joined) return joined;
    }
    return typeof aiContent.msg === 'string' ? aiContent.msg : '';
  }

  // ---------- ★ 生成完成红点（未读完成状态）----------
// 主窗口「带着地图学习」入口红点：
// 流结束即标记未读（sessionStorage 持久化，页面刷新不丢），
// 用户打开弹窗（AdvancedLearning mounted）后清除。
const UNSEEN_KEY = 'kg_flow_unseen';

function readUnseen() {
  try { return JSON.parse(sessionStorage.getItem(UNSEEN_KEY) || '[]'); }
  catch (e) { return []; }
}
function writeUnseen(list) {
  try { sessionStorage.setItem(UNSEEN_KEY, JSON.stringify(list)); } catch (e) {}
}

// 流结束时由 service 内部调用：标记该图谱有「已完成未读」的生成
function markFlowUnseen(flowKey) {
  const list = readUnseen();
  if (!list.includes(flowKey)) {
    list.push(flowKey);
    writeUnseen(list);
    notify(); // 主窗口订阅者立即收到 → 红点亮起
  }
}

// 主窗口查询：是否存在未读的已完成生成
export function hasUnseenFlows() {
  return readUnseen().length > 0;
}

// 用户打开弹窗后调用：清除全部未读标记（红点熄灭）
export function clearUnseenFlows() {
  if (readUnseen().length) {
    writeUnseen([]);
    notify(); // 通知主窗口订阅者刷新
  }
}

// ---------- ★ 流快照持久化（解决生产环境整页刷新后内存任务丢失） ----------
// sessionStorage 保存各流的终态元数据 + lastHtml，重开后可恢复"已完成/失败"的现场。
// 进行中的流刷新后无法续跑（请求中断），只恢复其快照内容供查看。
const SNAPSHOT_KEY = 'kg_flow_snapshot';
const SNAPSHOT_MAX_AGE = 24 * 60 * 60 * 1000; // 24h 过期

function readSnapshots() {
  try {
    const all = JSON.parse(sessionStorage.getItem(SNAPSHOT_KEY) || '{}');
    const now = Date.now();
    let dirty = false;
    for (const k of Object.keys(all)) {
      if (!all[k] || now - all[k].savedAt > SNAPSHOT_MAX_AGE) {
        delete all[k];
        dirty = true;
      }
    }
    if (dirty) writeSnapshots(all);
    return all;
  } catch (e) { return {}; }
}

function writeSnapshots(all) {
  try { sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify(all)); } catch (e) {}
}

// ★ 快照某个流（仅终态或有内容时才有意义）
function snapshotFlow(key) {
  const t = flow.tasks[key];
  if (!t) return;
  // 进行中且无任何内容 → 不值得快照
  if (!t.done && !t.failed && !t.lastHtml) return;
  const all = readSnapshots();
  all[key] = {
    type: t.type,
    done: !!t.done,
    failed: !!t.failed,
    errText: t.errText || '',
    busy: !!t.busy,
    emptyResult: !!t.emptyResult,
    lastHtml: t.lastHtml || '',
    finalMsg: t.finalMsg || '',
    savedAt: Date.now(),
  };
  writeSnapshots(all);
}

// ★ 从快照恢复为"只读流"对象（无活的请求，仅供视图渲染）
export function getFlowOrSnapshot(key) {
  const t = getFlow(key);
  if (t) return t;
  const snap = readSnapshots()[key];
  if (!snap) return null;
  return { ...snap, snapshotOnly: true };
}

// ★ 节流快照：流式过程中调用，2 秒最多落盘一次
let _snapTimer = null;
function scheduleSnapshot(key) {
  if (_snapTimer) return;
  _snapTimer = setTimeout(() => {
    _snapTimer = null;
    // 把当前所有有内容的流都刷一遍（开销小，sessionStorage 写入 ~几十 KB 级）
    for (const k of Object.keys(flow.tasks)) snapshotFlow(k);
  }, 2000);
}
